/* ScratchDialog — prise plein écran qui ouvre UN ticket à gratter.
   Au montage : POST /scratch (le dé est lancé côté serveur), puis la carte
   à gratter révèle le résultat sous le vernis. Gère aussi les cas bloqués
   (cooldown / déjà gagné / épuisé) renvoyés par le serveur.

   Le ticket est monté dans <body> par un portail, et ce n'est pas un détail :
   ScanPage enveloppe le bloc opt-in dans un `motion.div` qui anime son `filter`
   pour flouter la zone tant que l'AdGate n'est pas passé (ScanPage.jsx). Un
   `filter` autre que `none` — « blur(0px) » compris, la valeur laissée une fois
   le gate ouvert — fait de cet élément le BLOC CONTENEUR de ses descendants en
   `position: fixed`. Sans portail, `fixed inset-0` ne désigne donc pas l'écran
   mais la boîte de la carte opt-in : le ticket restait enfermé dedans, quelle
   que soit sa taille demandée.

   Le ticket occupe tout l'écran et la croix est la SEULE sortie : plus de
   fermeture au clic hors du ticket. Le grattage est un geste large, souvent
   imprécis, qui déborde forcément de la zone à gratter — le moindre
   débordement fermait le ticket en plein jeu, et le tirage était consommé
   côté serveur. */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatErr, PASSENGER_TIMEOUT_MS } from "@/lib/api";
import ScratchCard from "@/components/scan/ScratchCard";
import { fillTemplate } from "@/lib/defaults";
import { Loader2, X, Clock, PartyPopper } from "lucide-react";

const fmtCountdown = (secs) => {
  const s = Math.max(0, secs | 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}h ${pad(m)}m ${pad(sec)}s` : `${pad(m)}m ${pad(sec)}s`;
};

export default function ScratchDialog({
  ticket,
  scanEventId,
  phone,
  firstName,
  texts,
  foilFallback = "#FFC247",
  onClose,
  onResult,
}) {
  const [state, setState] = useState("loading"); // loading | ready | blocked | error
  const [result, setResult] = useState(null);
  const [block, setBlock] = useState(null); // { code, message, retryAfterSeconds }
  const [remaining, setRemaining] = useState(0);
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return; // évite le double-appel en StrictMode
    requested.current = true;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post(
          "/scratch",
          { scanEventId, phone, drawId: ticket.id, firstName },
          { timeout: PASSENGER_TIMEOUT_MS }
        );
        if (!alive) return;
        setResult(data);
        setState("ready");
        onResult && onResult(ticket.id, { done: true, instantWin: data.instantWin });
      } catch (err) {
        if (!alive) return;
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && ["cooldown", "won", "exhausted"].includes(detail.code)) {
          setBlock(detail);
          setRemaining(detail.retryAfterSeconds ?? 0);
          setState("blocked");
          onResult && onResult(ticket.id, { blocked: detail });
        } else {
          setBlock({ message: formatErr(err) });
          setState("error");
        }
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compte à rebours vivant pour le cooldown
  useEffect(() => {
    if (state !== "blocked" || block?.code !== "cooldown" || !block?.retryAfterSeconds) return;
    const t = setInterval(() => setRemaining((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [state, block]);

  /* Le fond de page ne doit plus défiler derrière le ticket : sans ce verrou,
     un geste de grattage qui déborde emporte la page du dessous et le passager
     perd sa surface en cours de route. */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // La croix reste atteignable au clavier ; Échap la double sur grand écran,
  // où un plein écran sans échappatoire clavier serait un piège.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const foil =
    (result?.instantWin && result?.sponsor?.primaryColor) ||
    ticket.sponsor?.primaryColor ||
    foilFallback;

  const message = result
    ? result.instantWin
      ? result.prize
        ? fillTemplate(texts.winTemplate, { name: firstName, prize: result.prize })
        : // Lot sans description (visuel seul) : message calculé côté serveur
          result.message || fillTemplate(texts.winTemplate, { name: firstName, prize: "" }).trim()
      : fillTemplate(texts.loseTemplate, { name: firstName, prize: result.prize || ticket.prizeDescription || "" })
    : "";

  return createPortal(
    <AnimatePresence>
      {/* Prise plein écran bord à bord : tant qu'il est ouvert, le ticket EST
          la page. L'aplat d'encre ne se voit plus que le temps du fondu
          d'entrée — la surface blanche du ticket le recouvre ensuite jusqu'aux
          quatre bords, encoche et barre de geste comprises.

          Le contenu défile DANS l'overlay. Un conteneur non défilant rognait le
          ticket en haut ET en bas dès qu'il dépassait la hauteur de l'écran :
          sur un petit téléphone, le bouton de sauvegarde du ticket gagnant
          devenait tout simplement inatteignable. `min-h-full` remplit l'écran
          tant que le contenu tient, et le laisse défiler au-delà. */}
      <motion.div
        className="fixed inset-0 z-50 overflow-y-auto overscroll-contain"
        style={{ background: "var(--color-ink, #16181D)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="scratch-dialog"
      >
        {/* Plus d'échelle à l'entrée : un plein écran qui grandit depuis 94 %
            laisse voir ses bords pendant l'animation et trahit la carte. */}
        <motion.div
          className={`relative flex min-h-full w-full flex-col ${
            state === "ready" ? "" : "bg-card"
          }`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
        >
          {state !== "ready" && (
            <button
              onClick={onClose}
              data-testid="scratch-dialog-close"
              aria-label="Fermer"
              className="absolute z-20 grid place-items-center rounded-full bg-[#16181D] text-white shadow-[0_4px_20px_rgba(0,0,0,0.35)] ring-[3px] ring-background transition-transform hover:scale-105 active:scale-95 touch-manipulation"
              style={{
                top: "max(clamp(0.65rem, 2.8vw, 1rem), env(safe-area-inset-top))",
                right: "max(clamp(0.65rem, 2.8vw, 1rem), env(safe-area-inset-right))",
                width: "clamp(2.75rem, 12vw, 3.25rem)",
                height: "clamp(2.75rem, 12vw, 3.25rem)",
              }}
            >
              <X
                className="shrink-0"
                style={{
                  width: "clamp(1.25rem, 5.5vw, 1.5rem)",
                  height: "clamp(1.25rem, 5.5vw, 1.5rem)",
                }}
                strokeWidth={3}
              />
            </button>
          )}

          {state === "ready" && result ? (
            <ScratchCard
              foilColor={typeof foil === "string" && foil.startsWith("#") ? foil : foilFallback}
              isWin={result.instantWin}
              prizeMessage={message}
              sponsor={result.sponsor || ticket.sponsor}
              /* Le bandeau sponsor descend DANS le ticket : hors de la carte
                 centrée d'avant, il n'y a plus de fond sombre au-dessus pour le
                 porter. Il reste au-dessus du vernis, donc lisible avant même
                 le premier coup d'ongle — c'est l'exposition que l'annonceur
                 achète. */
              sponsorLabel={ticket.sponsor ? texts.sponsorBannerLabel : null}
              prizeImageUrl={result.instantWin ? result.prizeImageUrl : null}
              collectionTerms={result.instantWin ? result.collectionTerms : null}
              onClose={onClose}
            />
          ) : (
            /* États sans ticket : même plein écran, message centré dans la
               hauteur plutôt que posé en haut d'une carte. */
            <div
              className="flex flex-1 flex-col items-center justify-center px-6 text-center"
              style={{
                paddingTop: "max(4.5rem, calc(env(safe-area-inset-top) + 4rem))",
                paddingBottom: "max(2rem, env(safe-area-inset-bottom))",
              }}
            >
              {ticket.sponsor && (
                <div className="mb-5 text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground">
                  {texts.sponsorBannerLabel}{" "}
                  <span className="font-semibold">{ticket.sponsor.name}</span>
                </div>
              )}

              {state === "loading" && (
                <div className="flex flex-col items-center text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="mt-3 text-sm">Préparation de votre ticket…</span>
                </div>
              )}

              {state === "blocked" && block && (
                <div>
                  <div
                    className="mx-auto mb-4 h-14 w-14 rounded-full grid place-items-center"
                    style={{ background: "var(--color-muted, #f1f1f1)" }}
                  >
                    {block.code === "won" ? (
                      <PartyPopper className="h-7 w-7" style={{ color: "var(--color-brand)" }} />
                    ) : (
                      <Clock className="h-7 w-7" style={{ color: "var(--color-brand)" }} />
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-bold">
                    {block.code === "won"
                      ? "Déjà gagné 🎉"
                      : block.code === "exhausted"
                      ? "Lot épuisé"
                      : "Ticket déjà gratté"}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">{block.message}</p>
                  {block.code === "cooldown" && block.retryAfterSeconds != null && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">
                        Nouveau grattage dans
                      </div>
                      <div
                        className="mt-1 font-heading text-3xl font-bold tabular-nums"
                        style={{ color: "var(--color-brand)" }}
                        data-testid="scratch-dialog-countdown"
                      >
                        {fmtCountdown(remaining)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {state === "error" && block && (
                <div>
                  <p className="text-sm" style={{ color: "var(--color-error, #D92D20)" }}>
                    {block.message}
                  </p>
                  <button
                    onClick={onClose}
                    className="mt-4 h-10 px-5 rounded-full bg-foreground text-background text-sm font-medium"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
